/* The Organic Butchery — component-product-v2.js
   Product page v2 (A/B variant): weight rows or variant picker, theme-built Subscribe /
   One-time selector, total, mobile gallery counter.
   Add to cart itself is handled by theme.js (form[data-product-form]); it
   reads the hidden name="selling_plan" input here, which is only enabled
   while "Subscription" is selected. */

(() => {
  const form = document.querySelector('[data-pdp2-form]');
  const jsonEl = document.querySelector('[data-pdp2-json]');
  const data = jsonEl ? JSON.parse(jsonEl.textContent) : null;

  /* ---------- gallery: mobile slider (peek, counter, arrows + dots underneath) ---------- */
  const track = document.querySelector('[data-pdp2-track]');
  const counter = document.querySelector('[data-pdp2-counter]');
  const dots = [...document.querySelectorAll('[data-pdp2-dot]')];
  const slideNav = [...document.querySelectorAll('[data-pdp2-slide]')];
  if (track && track.children.length > 1) {
    const total = track.children.length;
    /* one slide = its width + the gap; slides are 88% wide so the next one peeks in */
    const step = () => {
      const [a, b] = track.children;
      return Math.max(1, b.offsetLeft - a.offsetLeft);
    };
    const current = () => {
      if (track.scrollLeft >= track.scrollWidth - track.clientWidth - 2) return total - 1;
      return Math.min(total - 1, Math.round(track.scrollLeft / step()));
    };
    const goTo = (i) => track.scrollTo({ left: Math.max(0, Math.min(total - 1, i)) * step(), behavior: 'smooth' });
    const update = () => {
      const i = current();
      if (counter) counter.textContent = `${i + 1} / ${total}`;
      dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
      track.parentElement.classList.toggle('is-end', i === total - 1);
      slideNav.forEach((btn) => {
        btn.disabled = Number(btn.dataset.pdp2Slide) < 0 ? i === 0 : i === total - 1;
      });
    };
    track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    dots.forEach((d) => d.addEventListener('click', () => goTo(Number(d.dataset.pdp2Dot))));
    slideNav.forEach((btn) => btn.addEventListener('click', () => goTo(current() + Number(btn.dataset.pdp2Slide))));
  }

  /* ---------- zoom viewer: open on any image, swipe/arrows between, tap to zoom, drag to pan ---------- */
  const zoom = document.querySelector('[data-pdp2-zoom]');
  if (zoom && typeof zoom.showModal === 'function') {
    const zTrack = zoom.querySelector('[data-pdp2-zoom-track]');
    const slides = [...zoom.querySelectorAll('[data-pdp2-zoom-slide]')];
    const zCount = zoom.querySelector('[data-pdp2-zoom-count]');
    const prev = zoom.querySelector('[data-pdp2-zoom-step="-1"]');
    const next = zoom.querySelector('[data-pdp2-zoom-step="1"]');
    const index = () => Math.min(slides.length - 1, Math.round(zTrack.scrollLeft / Math.max(1, zTrack.clientWidth)));
    const sync = () => {
      const i = index();
      if (zCount) zCount.textContent = slides.length > 1 ? `${i + 1} / ${slides.length}` : '';
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === slides.length - 1;
    };
    const unzoom = () => {
      slides.forEach((s) => {
        s.classList.remove('is-zoomed', 'is-dragging');
        const im = s.querySelector('img');
        if (im) im.style.width = '';
      });
      zoom.classList.remove('is-zoomed');
    };
    const go = (i, smooth = true) => {
      unzoom();
      zTrack.scrollTo({ left: i * zTrack.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
    };

    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-pdp2-zoom-open]');
      if (!opener) return;
      zoom.showModal();
      go(Number(opener.dataset.pdp2ZoomOpen) || 0, false);
      sync();
    });
    zoom.querySelector('[data-pdp2-zoom-close]')?.addEventListener('click', () => zoom.close());
    zoom.addEventListener('close', unzoom);
    [prev, next].forEach((b) => b && b.addEventListener('click', () => go(index() + Number(b.dataset.pdp2ZoomStep))));
    zoom.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(Math.max(0, index() - 1)); }
      if (e.key === 'ArrowRight') { e.preventDefault(); go(Math.min(slides.length - 1, index() + 1)); }
    });
    zTrack.addEventListener('scroll', () => requestAnimationFrame(sync), { passive: true });

    /* tap/click toggles zoom at the point tapped; a drag (mouse) pans and doesn't toggle */
    let drag = null;
    slides.forEach((slide) => {
      const img = slide.querySelector('img');
      slide.addEventListener('click', (e) => {
        if (drag && drag.moved) { drag = null; return; }
        drag = null;
        if (slide.classList.contains('is-zoomed')) { unzoom(); return; }
        const box = img.getBoundingClientRect();
        if (e.clientX < box.left || e.clientX > box.right || e.clientY < box.top || e.clientY > box.bottom) return;
        const rx = (e.clientX - box.left) / box.width;
        const ry = (e.clientY - box.top) / box.height;
        /* 2.5x the fitted size, but no further than 1.5x the file's own width */
        img.style.width = `${Math.round(Math.min(box.width * 2.5, Math.max(img.naturalWidth || 0, box.width * 1.5)))}px`;
        slide.classList.add('is-zoomed');
        zoom.classList.add('is-zoomed', 'hint-done');
        /* centre the tapped point once the bigger image has laid out */
        requestAnimationFrame(() => {
          slide.scrollLeft = rx * img.offsetWidth - slide.clientWidth / 2;
          slide.scrollTop = ry * img.offsetHeight - slide.clientHeight / 2;
        });
      });
      slide.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || !slide.classList.contains('is-zoomed')) return;
        drag = { x: e.clientX, y: e.clientY, left: slide.scrollLeft, top: slide.scrollTop, moved: false };
        slide.classList.add('is-dragging');
      });
      slide.addEventListener('pointermove', (e) => {
        if (!drag || e.pointerType !== 'mouse') return;
        const dx = e.clientX - drag.x;
        const dy = e.clientY - drag.y;
        if (Math.abs(dx) + Math.abs(dy) > 4) drag.moved = true;
        slide.scrollLeft = drag.left - dx;
        slide.scrollTop = drag.top - dy;
      });
      const endDrag = () => slide.classList.remove('is-dragging');
      slide.addEventListener('pointerup', endDrag);
      slide.addEventListener('pointerleave', () => { endDrag(); if (drag && !drag.moved) drag = null; });
    });
  }

  /* rating line "(67)": scroll to the reviews, clear of the sticky header + nav
     (Judge.me handles its own #judgeme_product_reviews hash with a smaller offset) */
  document.addEventListener('click', (e) => {
    const link = e.target.closest('.pdp2__count');
    const target = link && document.getElementById('judgeme_product_reviews');
    if (!target) return;
    e.preventDefault();
    /* where the sticky bars sit once scrolled: their sticky top + height */
    const sticky = [...document.querySelectorAll('.shopify-section-group-header-group')]
      .map((el) => [el, getComputedStyle(el)])
      .filter(([, cs]) => cs.position === 'sticky')
      .reduce((m, [el, cs]) => Math.max(m, (parseFloat(cs.top) || 0) + el.offsetHeight), 0);
    window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - sticky - 24, behavior: 'smooth' });
  });

  if (!form || !data) return;

  const idInput = form.querySelector('[data-variant-id]');
  const planInput = form.querySelector('[data-pdp2-selling-plan]');
  const purchase = document.querySelector('[data-pdp2-purchase]');
  const planSelect = document.querySelector('[data-pdp2-plan]');
  const $ = (sel) => document.querySelector(sel);
  const strings = window.pdp2Strings || {};

  let variant = data.variants.find((v) => String(v.id) === idInput.value) || data.variants[0];
  let unavailable = false; /* Variant picker: a combination with no variant */
  let mode = purchase ? (purchase.dataset.default === 'sub' ? 'sub' : 'one') : 'one';
  let planId = planSelect ? planSelect.value : (purchase ? purchase.dataset.recommended : '');

  /* Appstle may still inject its own widget into the form via its app embed:
     hide it and strip its selling_plan names so only our input submits. */
  const neutralizeAppstle = () => {
    document.querySelectorAll('.appstle_sub_widget, .appstle-tootltip-parent').forEach((el) => {
      el.hidden = true;
      el.setAttribute('aria-hidden', 'true');
    });
    document.querySelectorAll('.appstle_sub_widget input, .appstle_sub_widget select, form [name="selling_plan"]:not([data-pdp2-selling-plan])').forEach((el) => {
      el.disabled = true;
      if (el.name === 'selling_plan') el.removeAttribute('name');
    });
  };
  neutralizeAppstle();
  document.addEventListener('AppstleSubscription:SubscriptionWidget:widgetInitialised', neutralizeAppstle);
  form.addEventListener('submit', neutralizeAppstle, true);
  /* Appstle injects after load (and can re-inject); watching just this form's
     children is cheap and catches it whenever it lands. */
  if ('MutationObserver' in window) {
    new MutationObserver(() => {
      if (form.querySelector('.appstle_sub_widget:not([hidden]), [name="selling_plan"]:not([data-pdp2-selling-plan])')) neutralizeAppstle();
    }).observe(form, { childList: true, subtree: true });
  }

  /* the basket's subscription frequency (snippets/cart-plan-check.liquid, inside the
     cart drawer): pre-selected and marked in its option, so a new item joins the same
     delivery. Picking another one shows a one-line hint — theme.js then moves the
     basket's subscription items to it on add. Never overrides the customer's own pick. */
  const freqBox = document.querySelector('.pdp2__freq');
  const planHint = document.querySelector('[data-pdp2-plan-hint]');
  const basketPlan = () => {
    const el = document.querySelector('[data-cart-basket-plan]');
    return el ? { id: el.dataset.cartBasketPlan, name: el.dataset.cartBasketPlanName || '' } : null;
  };
  const optionFor = (id) => (planSelect && id ? planSelect.querySelector(`option[value="${CSS.escape(String(id))}"]`) : null);
  let userPickedPlan = false;

  const markBasketOption = () => {
    const basket = basketPlan();
    const note = freqBox ? freqBox.dataset.basketLabel : '';
    planSelect.querySelectorAll('option').forEach((o) => {
      o.textContent = basket && note && o.value === basket.id ? `${o.dataset.name} (${note})` : o.dataset.label;
    });
  };

  const renderPlanHint = (activePlan) => {
    if (!planHint) return;
    const basket = basketPlan();
    const clash = basket && activePlan && String(activePlan) !== basket.id && optionFor(basket.id);
    planHint.hidden = !clash;
    planHint.textContent = '';
    if (!clash) return;
    const chosen = optionFor(activePlan);
    planHint.append(`Your other subscription items will switch to ${(chosen ? chosen.dataset.name : '').toLowerCase()} too, so everything arrives together. `);
    const keep = document.createElement('button');
    keep.type = 'button';
    keep.className = 'pdp2__freq-keep';
    keep.dataset.pdp2KeepBasket = '';
    keep.textContent = `Keep ${basket.name.toLowerCase()}?`;
    planHint.append(keep);
  };

  const setText = (el, text) => { if (el) el.textContent = text || ''; };
  const plansFor = (v) => v.plans || {};
  const hasPlans = (v) => Object.keys(plansFor(v)).length > 0;

  const render = () => {
    const plans = plansFor(variant);
    /* the chosen frequency, or the variant's first plan if it doesn't offer that one */
    const usePlanId = plans[planId] ? String(planId) : Object.keys(plans)[0];
    const plan = usePlanId ? plans[usePlanId] : null;
    const subActive = mode === 'sub' && purchase && plan && hasPlans(variant);

    /* subscription card */
    if (purchase) {
      const maxSave = Object.values(plans).reduce((m, p) => Math.max(m, p.save || 0), 0);
      const pill = $('[data-pdp2-save]');
      if (pill) {
        pill.textContent = (purchase.dataset.saveLabel || '').replace('[percent]', String(maxSave));
        pill.hidden = !maxSave;
      }
      setText($('[data-pdp2-sub-price]'), plan ? plan.price : variant.price);
      setText($('[data-pdp2-sub-compare]'), plan && plan.save > 0 ? variant.ref : '');
      setText($('[data-pdp2-one-price]'), variant.price);
      setText($('[data-pdp2-one-compare]'), variant.compare || '');
      /* one-time sale pill: the variant's saving against its compare-at price */
      const onePill = $('[data-pdp2-one-save]');
      if (onePill) {
        onePill.textContent = variant.save > 0 ? (purchase.dataset.oneSaveLabel || '').replace('[percent]', String(variant.save)).replace('[amount]', variant.saveAmount || '') : '';
        onePill.hidden = !(variant.save > 0);
      }
      purchase.querySelectorAll('[data-pdp2-opt]').forEach((opt) => {
        opt.classList.toggle('is-selected', opt.dataset.pdp2Opt === (subActive ? 'sub' : 'one'));
      });
      if (planSelect) markBasketOption();
      renderPlanHint(subActive ? usePlanId : '');
      purchase.hidden = !hasPlans(variant);
    }

    /* the selling plan only submits while Subscription is chosen */
    if (planInput) {
      planInput.value = subActive ? usePlanId : '';
      planInput.disabled = !subActive;
    }

    /* total */
    setText($('[data-pdp2-total]'), subActive ? plan.price : variant.price);
    setText($('[data-pdp2-total-compare]'), subActive ? (plan.save > 0 ? variant.ref : '') : (variant.compare || ''));
    /* Spend more, save more panel: full-price variants only, hidden when there's a compare-at price */
    const offer = document.querySelector('[data-pdp2-offer]');
    if (offer) offer.hidden = Boolean(variant.compare);

    /* sale % by the total, only when there's no Subscribe / One-time card to carry it */
    const totalPill = $('[data-pdp2-total-save]');
    if (totalPill) {
      const showTotalPill = !subActive && variant.save > 0 && (!purchase || purchase.hidden);
      totalPill.textContent = showTotalPill ? (totalPill.dataset.label || '').replace('[percent]', String(variant.save)).replace('[amount]', variant.saveAmount || '') : '';
      totalPill.hidden = !showTotalPill;
    }

    /* button */
    const atc = form.querySelector('[data-atc]');
    const label = form.querySelector('[data-atc-label]');
    if (atc) atc.disabled = unavailable || !variant.available;
    if (label) setText(label, unavailable ? strings.unavailable : (variant.available ? strings.add : strings.sold_out));

    /* price per kg under the Variant picker */
    setText($('[data-pdp2-unit]'), unavailable ? '' : variant.unit);
  };

  const selectVariant = (v) => {
    unavailable = !v;
    if (v) {
      variant = v;
      idInput.value = variant.id;
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
    render();
  };

  document.addEventListener('change', (e) => {
    const t = e.target;
    if (t.matches('[data-pdp2-variant]')) {
      document.querySelectorAll('.pdp2__var').forEach((row) => {
        row.classList.toggle('is-selected', row.contains(t));
      });
      selectVariant(data.variants.find((v) => String(v.id) === t.value) || variant);
    }
    if (t.matches('[data-pdp2-option]')) {
      const opts = [];
      document.querySelectorAll('[data-pdp2-option]:checked').forEach((i) => { opts[i.dataset.pdp2Option] = i.value; });
      selectVariant(data.variants.find((v) => v.options.every((o, i) => o === opts[i])));
    }
    if (t.matches('[data-pdp2-mode]')) { mode = t.value; render(); }
    if (t.matches('[data-pdp2-plan]')) {
      planId = t.value;
      if (e.isTrusted) userPickedPlan = true;
      render();
    }
  });

  /* clicking anywhere on the subscription body (not just the radio) selects it */
  if (purchase) {
    purchase.addEventListener('click', (e) => {
      const opt = e.target.closest('[data-pdp2-opt]');
      if (!opt || e.target.closest('select, label')) return;
      const radio = opt.querySelector('[data-pdp2-mode]');
      if (radio && !radio.checked) { radio.checked = true; mode = radio.value; render(); }
    });
  }

  /* quantity stepper (optional) */
  form.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-change]');
    if (!btn) return;
    const input = form.querySelector('input[data-qty-value]');
    input.value = Math.max(1, (parseInt(input.value, 10) || 1) + parseInt(btn.dataset.qtyChange, 10));
  });

  const followBasketPlan = () => {
    const basket = basketPlan();
    if (basket && !userPickedPlan && optionFor(basket.id)) {
      planId = basket.id;
      planSelect.value = basket.id;
    }
    render();
  };
  /* "Keep every 2 weeks" in the hint: back to the basket's frequency */
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-pdp2-keep-basket]')) return;
    userPickedPlan = false;
    followBasketPlan();
    if (planSelect) planSelect.focus();
  });
  followBasketPlan();
  document.addEventListener('cart:refreshed', followBasketPlan);
})();
